import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminShell } from '../components/layout/AdminShell';
import { config } from '../lib/config';
import {
  changeEmail,
  changePassword,
  confirmEmailChange,
  setAccountAlias,
  updateClientProfile,
} from '../lib/identityAuth';
import { fetchMe } from '../lib/identityRegistry';
import { PasswordField } from '../components/PasswordField';

const ALIAS_MAX_LENGTH = 64;

export function AccountPage(): JSX.Element {
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: fetchMe });

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [aliasOpen, setAliasOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailStage, setEmailStage] = useState<'request' | 'verify'>('request');
  const [emailMessage, setEmailMessage] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [pageMessage, setPageMessage] = useState<string | null>(null);

  const [aliasDraft, setAliasDraft] = useState('');
  const [aliasError, setAliasError] = useState<string | null>(null);

  const [clientAddress, setClientAddress] = useState('');
  const [clientNit, setClientNit] = useState('');
  const [clientCity, setClientCity] = useState('');
  const [clientCountry, setClientCountry] = useState('');
  const [clientError, setClientError] = useState<string | null>(null);

  const passwordMutation = useMutation({
    mutationFn: () => changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setPasswordError(null);
      setCurrentPassword('');
      setNewPassword('');
      setPasswordOpen(false);
      setPasswordMessage(null);
      setPageMessage('Password updated.');
    },
    onError: (error) => {
      setPasswordMessage(null);
      setPasswordError(error instanceof Error ? error.message : 'Password change failed');
    },
  });

  const emailRequestMutation = useMutation({
    mutationFn: () => changeEmail(newEmail),
    onSuccess: (result) => {
      setEmailStage('verify');
      setEmailMessage(`Verification code sent to ${result.email}.`);
      setEmailError(null);
    },
    onError: (error) => {
      setEmailMessage(null);
      setEmailError(error instanceof Error ? error.message : 'Email change failed');
    },
  });

  const emailVerifyMutation = useMutation({
    mutationFn: () => confirmEmailChange(emailCode),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      setEmailStage('request');
      setEmailMessage(null);
      setEmailError(null);
      setNewEmail('');
      setEmailCode('');
      setEmailOpen(false);
      setPageMessage('Email address updated.');
    },
    onError: (error) => {
      setEmailMessage(null);
      setEmailError(error instanceof Error ? error.message : 'Verification failed');
    },
  });

  const aliasMutation = useMutation({
    mutationFn: () => setAccountAlias(aliasDraft.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      setAliasError(null);
      setAliasOpen(false);
      setPageMessage('Display name updated.');
    },
    onError: (error) => {
      setAliasError(error instanceof Error ? error.message : 'Display name update failed');
    },
  });

  const clientMutation = useMutation({
    mutationFn: () =>
      updateClientProfile({
        address: clientAddress.trim(),
        nit: clientNit.trim(),
        city: clientCity.trim(),
        country: clientCountry.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      setClientError(null);
      setClientOpen(false);
      setPageMessage('Client profile updated.');
    },
    onError: (error) => {
      setClientError(error instanceof Error ? error.message : 'Client profile update failed');
    },
  });

  if (meQuery.isLoading) {
    return (
      <div className="route-skeleton route-skeleton--fullscreen">
        <div className="route-skeleton__pulse" />
        <p>Loading account...</p>
      </div>
    );
  }

  if (meQuery.error || !meQuery.data) {
    return (
      <div className="center-screen">
        <div className="panel unauthorized">
          <h1>Unable to load profile</h1>
          <p className="panel__copy">
            {meQuery.error instanceof Error ? meQuery.error.message : 'Request failed'}
          </p>
        </div>
      </div>
    );
  }

  const me = meQuery.data;

  function resetPasswordForm() {
    setCurrentPassword('');
    setNewPassword('');
    setPasswordMessage(null);
    setPasswordError(null);
  }

  function closePassword() {
    if (passwordMutation.isPending) return;
    setPasswordOpen(false);
    resetPasswordForm();
  }

  function resetEmailForm() {
    setNewEmail('');
    setEmailCode('');
    setEmailStage('request');
    setEmailMessage(null);
    setEmailError(null);
  }

  function closeEmail() {
    if (emailRequestMutation.isPending || emailVerifyMutation.isPending) return;
    setEmailOpen(false);
    resetEmailForm();
  }

  function resetAliasForm() {
    setAliasDraft(me.alias ?? '');
    setAliasError(null);
  }

  function closeAlias() {
    if (aliasMutation.isPending) return;
    setAliasOpen(false);
    setAliasError(null);
  }

  function resetClientForm() {
    setClientAddress(me.client_address ?? '');
    setClientNit(me.client_nit ?? '');
    setClientCity(me.client_city ?? '');
    setClientCountry(me.client_country ?? '');
    setClientError(null);
  }

  function closeClient() {
    if (clientMutation.isPending) return;
    setClientOpen(false);
    setClientError(null);
  }

  function handlePasswordSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    passwordMutation.mutate();
  }

  function handleEmailRequest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    emailRequestMutation.mutate();
  }

  function handleEmailVerify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    emailVerifyMutation.mutate();
  }

  function handleAliasSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    aliasMutation.mutate();
  }

  function handleClientSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    clientMutation.mutate();
  }

  return (
    <AdminShell title="Sense AI — My account" subtitle={me.email}>
      <section className="panel admin-panel">
        <div className="panel-toolbar">
          <h2>Profile</h2>
          <div className="panel-toolbar__actions">
            <button
              className="primary-button"
              type="button"
              onClick={() => {
                setPageMessage(null);
                resetPasswordForm();
                setPasswordOpen(true);
              }}
            >
              Change password
            </button>
            <button
              className="ghost-button"
              type="button"
              onClick={() => {
                setPageMessage(null);
                resetAliasForm();
                setAliasOpen(true);
              }}
            >
              Edit display name
            </button>
            {me.is_master ? (
              <button
                className="ghost-button"
                type="button"
                onClick={() => {
                  setPageMessage(null);
                  resetClientForm();
                  setClientOpen(true);
                }}
              >
                Edit client profile
              </button>
            ) : null}
            {me.is_master ? (
              <button
                className="ghost-button"
                type="button"
                onClick={() => {
                  setPageMessage(null);
                  resetEmailForm();
                  setEmailOpen(true);
                }}
              >
                Change email
              </button>
            ) : null}
          </div>
        </div>

        <div className="admin-grid">
          <div className="stat-card">
            <p className="stat-card__label">Email</p>
            <p className="stat-card__value">{me.email}</p>
          </div>
          <div className="stat-card">
            <p className="stat-card__label">Display name</p>
            <p className="stat-card__value">{me.alias?.trim() ? me.alias : '—'}</p>
          </div>
          <div className="stat-card">
            <p className="stat-card__label">Role</p>
            <p className="stat-card__value">{me.role_name}</p>
          </div>
          <div className="stat-card">
            <p className="stat-card__label">Account master</p>
            <p className="stat-card__value">{me.is_master ? 'Yes' : 'No'}</p>
          </div>
          <div className="stat-card">
            <p className="stat-card__label">Client address</p>
            <p className="stat-card__value">{me.client_address?.trim() ? me.client_address : '—'}</p>
          </div>
          <div className="stat-card">
            <p className="stat-card__label">NIT</p>
            <p className="stat-card__value">{me.client_nit?.trim() ? me.client_nit : '—'}</p>
          </div>
          <div className="stat-card">
            <p className="stat-card__label">City</p>
            <p className="stat-card__value">{me.client_city?.trim() ? me.client_city : '—'}</p>
          </div>
          <div className="stat-card">
            <p className="stat-card__label">Country</p>
            <p className="stat-card__value">{me.client_country?.trim() ? me.client_country : '—'}</p>
          </div>
        </div>

        {pageMessage ? <p className="hint-text">{pageMessage}</p> : null}
        {!me.is_master ? (
          <p className="hint-text">
            Only the account master can change the account email address or client profile.
          </p>
        ) : null}
      </section>

      {passwordOpen ? (
        <div className="modal-backdrop" onClick={closePassword}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <h2>Change password</h2>
            <p className="modal-card__copy">Enter your current password and choose a new one.</p>
            <form className="invite-form" onSubmit={handlePasswordSubmit}>
              <PasswordField
                className="form-field"
                label="Current password"
                value={currentPassword}
                onChange={setCurrentPassword}
                autoComplete="current-password"
              />
              <PasswordField
                className="form-field"
                label="New password"
                value={newPassword}
                onChange={setNewPassword}
                minLength={config.passwordMinLength}
                autoComplete="new-password"
              />
              {passwordMessage ? <p className="hint-text">{passwordMessage}</p> : null}
              {passwordError ? <div className="form-error">{passwordError}</div> : null}
              <div className="modal-card__actions">
                <button
                  className="ghost-button"
                  type="button"
                  disabled={passwordMutation.isPending}
                  onClick={closePassword}
                >
                  Cancel
                </button>
                <button className="primary-button" disabled={passwordMutation.isPending} type="submit">
                  {passwordMutation.isPending ? 'Saving...' : 'Update password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {aliasOpen ? (
        <div className="modal-backdrop" onClick={closeAlias}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <h2>Edit display name</h2>
            <p className="modal-card__copy">
              This name is shown to others instead of your email. Leave blank to clear.
            </p>
            <form className="invite-form" onSubmit={handleAliasSubmit}>
              <label className="form-field">
                <span>Display name</span>
                <input
                  type="text"
                  maxLength={ALIAS_MAX_LENGTH}
                  value={aliasDraft}
                  onChange={(event) => setAliasDraft(event.target.value)}
                  autoComplete="nickname"
                />
              </label>
              {aliasError ? <div className="form-error">{aliasError}</div> : null}
              <div className="modal-card__actions">
                <button
                  className="ghost-button"
                  type="button"
                  disabled={aliasMutation.isPending}
                  onClick={closeAlias}
                >
                  Cancel
                </button>
                <button className="primary-button" disabled={aliasMutation.isPending} type="submit">
                  {aliasMutation.isPending ? 'Saving...' : 'Save display name'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {emailOpen ? (
        <div className="modal-backdrop" onClick={closeEmail}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <h2>Change email</h2>
            <p className="modal-card__copy">
              {emailStage === 'request'
                ? 'We will send a verification code to the new address.'
                : `Enter the code sent to ${newEmail}.`}
            </p>

            {emailStage === 'request' ? (
              <form className="invite-form" onSubmit={handleEmailRequest}>
                <label className="form-field">
                  <span>New email</span>
                  <input
                    type="email"
                    required
                    value={newEmail}
                    onChange={(event) => setNewEmail(event.target.value)}
                  />
                </label>
                {emailError ? <div className="form-error">{emailError}</div> : null}
                <div className="modal-card__actions">
                  <button
                    className="ghost-button"
                    type="button"
                    disabled={emailRequestMutation.isPending}
                    onClick={closeEmail}
                  >
                    Cancel
                  </button>
                  <button
                    className="primary-button"
                    disabled={emailRequestMutation.isPending}
                    type="submit"
                  >
                    {emailRequestMutation.isPending ? 'Sending...' : 'Send verification code'}
                  </button>
                </div>
              </form>
            ) : (
              <form className="invite-form" onSubmit={handleEmailVerify}>
                {emailMessage ? <p className="hint-text">{emailMessage}</p> : null}
                <label className="form-field">
                  <span>Verification code</span>
                  <input
                    required
                    value={emailCode}
                    onChange={(event) => setEmailCode(event.target.value)}
                  />
                </label>
                {emailError ? <div className="form-error">{emailError}</div> : null}
                <div className="modal-card__actions">
                  <button
                    className="ghost-button"
                    type="button"
                    disabled={emailVerifyMutation.isPending}
                    onClick={() => {
                      setEmailStage('request');
                      setEmailCode('');
                      setEmailMessage(null);
                      setEmailError(null);
                    }}
                  >
                    Back
                  </button>
                  <button
                    className="primary-button"
                    disabled={emailVerifyMutation.isPending}
                    type="submit"
                  >
                    {emailVerifyMutation.isPending ? 'Verifying...' : 'Confirm new email'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      ) : null}

      {clientOpen ? (
        <div className="modal-backdrop" onClick={closeClient}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <h2>Edit client profile</h2>
            <p className="modal-card__copy">
              Update the address, NIT, city, and country for your client account.
            </p>
            <form className="invite-form" onSubmit={handleClientSubmit}>
              <label className="form-field">
                <span>Address</span>
                <input
                  type="text"
                  required
                  value={clientAddress}
                  onChange={(event) => setClientAddress(event.target.value)}
                  autoComplete="street-address"
                />
              </label>
              <label className="form-field">
                <span>NIT</span>
                <input
                  type="text"
                  required
                  value={clientNit}
                  onChange={(event) => setClientNit(event.target.value)}
                />
              </label>
              <label className="form-field">
                <span>City</span>
                <input
                  type="text"
                  required
                  value={clientCity}
                  onChange={(event) => setClientCity(event.target.value)}
                  autoComplete="address-level2"
                />
              </label>
              <label className="form-field">
                <span>Country</span>
                <input
                  type="text"
                  required
                  value={clientCountry}
                  onChange={(event) => setClientCountry(event.target.value)}
                  autoComplete="country-name"
                />
              </label>
              {clientError ? <div className="form-error">{clientError}</div> : null}
              <div className="modal-card__actions">
                <button
                  className="ghost-button"
                  type="button"
                  disabled={clientMutation.isPending}
                  onClick={closeClient}
                >
                  Cancel
                </button>
                <button className="primary-button" disabled={clientMutation.isPending} type="submit">
                  {clientMutation.isPending ? 'Saving...' : 'Save client profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
}
